import { reportAreaName } from "utils/areaName";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDInput from "components/MDInput";
import { notifySalesUpdated, useSalesPolling } from "utils/salesSync";
import { Dialog, DialogTitle, DialogContent, DialogActions, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Checkbox, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { boxCount, packetCount, buildDeliveryLog, deliveryLogColors } from "utils/deliveryLog";
import PackBadges from "./PackBadges";
import { printDeliveryAssignmentsPdf } from "utils/printDeliveryAssignmentsPdf";
import { IconButton, Tooltip, FormControlLabel, Tabs, Tab } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BillDetailsDialog from "./BillDetailsDialog";
import AssignmentHistory from "./AssignmentHistory";
import DeliveryStatusReport from "./DeliveryStatusReport";
import PendingDeliveries from "./PendingDeliveries";
import DeliveryTransfers from "./DeliveryTransfers";
import { selectedAssignments, remainingAssignments, toggleAssignmentSelection } from "utils/deliveryAssignmentSelection";
import CompanyTypeSelect from "components/CompanyFilter/CompanyTypeSelect";
import { useCompanyTypeLookup } from "utils/companyTypes";

const API = (process.env.REACT_APP_API_URL);
const today = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); };
export default function DeliveryReport() {
  const [sales, setSales] = useState([]);
  const [boys, setBoys] = useState([]);
  const [selected, setSelected] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [removeIds, setRemoveIds] = useState([]);
  const [viewBill, setViewBill] = useState(null);
  const [viewGroup, setViewGroup] = useState(null);
  const [boy, setBoy] = useState("");
  const [assigneeType, setAssigneeType] = useState("");
  const [assigneeCompany, setAssigneeCompany] = useState("");
  const [assigneeCompanyType, setAssigneeCompanyType] = useState("");
  const { matches: companyMatchesType } = useCompanyTypeLookup();
  const marketingStaff = boys.filter((person) => String(person.id).startsWith("staff:") && (!person.staff_category || person.staff_category === "company_staff"));
  const staffCompanyIds = (person) => String(person.company_ids || person.company_id || "").split(",").map((id) => id.trim()).filter(Boolean);
  const companyMap = new Map();
  marketingStaff.forEach((person) => {
    const names = String(person.company_name || "").split(",").map((name) => name.trim());
    staffCompanyIds(person).forEach((id, index) => companyMap.set(id, names[index] || `Company ${id}`));
  });
  const assigneeCompanies = [...companyMap.entries()]
    .sort((a, b) => a[1].localeCompare(b[1]))
    .filter(([id]) => companyMatchesType(id, assigneeCompanyType));
  const eligibleAssignees = assigneeType === "delivery_boy"
    ? boys.filter((person) => !String(person.id).startsWith("staff:"))
    : marketingStaff.filter((person) => assigneeCompany && staffCompanyIds(person).includes(assigneeCompany));
  const [date, setDate] = useState(today);
  const [vehicle, setVehicle] = useState("");
  const [saving, setSaving] = useState(false);
  const [suspenseBusy, setSuspenseBusy] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [routeId, setRouteId] = useState("");
  const [suspenseFeedback, setSuspenseFeedback] = useState("");
  const [suspenseReason, setSuspenseReason] = useState("");
  const [message, setMessage] = useState("");
  const [historyTab, setHistoryTab] = useState("assignments");
  const busy = useRef(false);
  const load = useCallback(async () => {
    try {
      const responses = await Promise.all([fetch(API + "/staff/sales/by-date"), fetch(API + "/delivery-boy"), fetch(API + "/staff")]);
      if (responses.some((r) => !r.ok)) throw new Error("Unable to load delivery report");
      const [rows, staff, companyStaff] = await Promise.all(responses.map((r) => r.json()));
      setSales(rows.filter((row) => Number(row.in_suspense) !== 1)); setBoys([...staff.filter((b) => b.role === "delivery_boy" && Number(b.is_active) === 1), ...companyStaff.filter((person) => Number(person.is_active) === 1).map((person) => ({ ...person, id: "staff:" + person.id, name: person.name + " (Company staff)" }))]);
    } catch (err) { setError(err.message); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const [routeError, setRouteError] = useState("");
  useEffect(() => {
    // Send the login token explicitly: this page may call a server other than
    // the one the global fetch interceptor adds tokens for.
    const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
    fetch(API + "/staff/routes", { headers: token ? { Authorization: "Bearer " + token } : {} })
      .then(async (response) => {
        const rows = await response.json().catch(() => null);
        if (!response.ok) throw new Error((rows && rows.error) || "server returned " + response.status);
        const list = Array.isArray(rows) ? rows : [];
        setRoutes(list);
        setRouteError(list.length ? "" : "No routes yet. Create them in Staff Management → Create Route.");
      })
      .catch((err) => { setRoutes([]); setRouteError("Routes could not be loaded: " + err.message); });
  }, []);
  useSalesPolling(load);
  const stagedIds = useMemo(() => new Set(drafts.flatMap((draft) => draft.rows.map((row) => String(row.id)))), [drafts]);
  const availableSales = useMemo(() => sales.filter((row) => !stagedIds.has(String(row.id))), [sales, stagedIds]);
  // Area delivery priority from routes: the selected route first, then the
  // other routes (R 1, R 2 ...), each in its area priority order.
  const areaOrder = useMemo(() => {
    const ordered = [...routes].sort((a, b) =>
      Number(String(b.id) === String(routeId)) - Number(String(a.id) === String(routeId))
      || String(a.name).localeCompare(String(b.name), undefined, { numeric: true }));
    return [...new Set(ordered.flatMap((route) => (route.areas || []).map((area) => reportAreaName(area))))];
  }, [routes, routeId]);
  const areaRank = useMemo(() => new Map(areaOrder.map((area, index) => [area, index])), [areaOrder]);
  // Same priority without a selected route (R 1, R 2 ...) for saved assignments.
  const routeAreaOrder = useMemo(() => [...new Set([...routes]
    .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { numeric: true }))
    .flatMap((route) => (route.areas || []).map((area) => reportAreaName(area))))], [routes]);
  const byPriority = useCallback((a, b) => {
    const rankA = areaRank.has(reportAreaName(a.location_name)) ? areaRank.get(reportAreaName(a.location_name)) : Number.MAX_SAFE_INTEGER;
    const rankB = areaRank.has(reportAreaName(b.location_name)) ? areaRank.get(reportAreaName(b.location_name)) : Number.MAX_SAFE_INTEGER;
    return rankA - rankB || reportAreaName(a.location_name).localeCompare(reportAreaName(b.location_name));
  }, [areaRank]);
  const pending = useMemo(() => [...new Map(availableSales.filter((row) => ["packing_done", "returned"].includes(row.packaging_status)).map((row) => [String(row.id), row])).values()].sort(byPriority), [availableSales, byPriority]);
  const draftBoys = [...new Set(drafts.map((draft) => draft.boy))];
  const billsForRoute = (route) => {
    const areas = new Set((route?.areas || []).map((area) => reportAreaName(area)));
    return pending.filter((row) => areas.has(reportAreaName(row.location_name)));
  };
  // Choosing a route selects all its pending bills; then use Assign as usual.
  // To send one bill to another delivery boy, untick it (eye icon) before Assign.
  const selectRoute = (value) => {
    setRouteId(value);
    const route = routes.find((item) => String(item.id) === String(value));
    if (!route) return;
    const ids = billsForRoute(route).map((row) => String(row.id));
    setSelected(ids);
    setError("");
    setMessage(ids.length
      ? route.name + ": " + ids.length + " bills selected. Click Assign to choose the delivery boy or company staff."
      : route.name + " has no pending bills.");
  };
  const chosen = pending.filter((row) => selected.includes(String(row.id)));
  const selectedDrafts = selectedAssignments(drafts, removeIds);
  const selectedBillCount = selectedDrafts.reduce((sum, draft) => sum + draft.rows.length, 0);
  const cellBills = (type, cells, column) => {
    if (!column || !["counts", "summary", "total"].includes(type)) return [];
    const company = type === "counts" || type === "summary" ? cells[0] : null;
    const area = (type === "counts" || cells[0] === "Area totals") && column < report.width - 1 ? report.rows[0].cells[column] : null;
    return pending.filter((row) => (!company || String(row.company_name || "Company not assigned").trim() === company) && (!area || reportAreaName(row.location_name) === area));
  };
  const toggle = (rows) => { const ids = rows.map((row) => String(row.id)); setSelected((prev) => ids.every((id) => prev.includes(id)) ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])]); };
  const stage = () => {
    if (busy.current || !chosen.length) return;
    if (!assigneeType || !eligibleAssignees.some((person) => String(person.id) === boy)) { setError("Choose an assignment type and an employee from the selected company."); return; }
    if (!boy || !date || !vehicle.trim()) { setError("Select delivery boy or company staff, date and vehicle number."); return; }
    setDrafts((prev) => [...prev, { id: Date.now(), boy, name: boys.find((person) => String(person.id) === boy)?.name || "Delivery Boy", date, vehicle: vehicle.trim(), rows: chosen }]);
    setSelected([]); setRouteId(""); setOpen(false); setError("");
    setMessage(chosen.length + " bills added to View. Use Submit in View to send assignments.");
  };
  const toggleDraftArea = (ids) => {
    if (busy.current) return;
    setRemoveIds((prev) => toggleAssignmentSelection(prev, ids));
  };
  const removeSelectedAreas = (draftId) => {
    if (busy.current) return;
    const ids = drafts.find((draft) => draft.id === draftId)?.rows
      .map((row) => String(row.id)).filter((id) => removeIds.includes(id)) || [];
    if (!ids.length) return;
    setDrafts((prev) => prev.map((draft) => draft.id === draftId
      ? { ...draft, rows: draft.rows.filter((row) => !ids.includes(String(row.id))) } : draft)
      .filter((draft) => draft.rows.length));
    setRemoveIds((prev) => prev.filter((id) => !ids.includes(id)));
    setError("");
    setMessage(ids.length + " bills removed from this draft and returned to Report for reassignment.");
  };
  const submit = async () => {
    if (busy.current || !selectedBillCount) return;
    busy.current = true; setSaving(true); setError(""); setMessage("");
    const moved = []; const failed = [];
    for (const draft of selectedDrafts) {
    for (const row of draft.rows) {
      try {
        const response = await fetch(API + "/staff/sales/" + row.id + "/packaging", {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ packagingStatus: "out_for_delivery", expectedStatus: row.packaging_status, deliveryBoyId: String(draft.boy).startsWith("staff:") ? null : Number(draft.boy), deliveryStaffId: String(draft.boy).startsWith("staff:") ? Number(String(draft.boy).slice(6)) : null, deliveryDate: draft.date, vehicleNo: draft.vehicle }),
        });
        if (!response.ok) { const result = await response.json(); throw new Error(result.error || "Assignment failed"); }
        moved.push(String(row.id));
      } catch (err) { failed.push(String(row.invoice_number || row.id) + ": " + err.message); }
    }
    }
    // Hide successful bills immediately, even if the refresh fails.
    setSales((prev) => prev.filter((row) => !moved.includes(String(row.id))));
    setDrafts((prev) => remainingAssignments(prev, moved, removeIds));
    setRemoveIds([]);
    setMessage(moved.length + " bills assigned and sent to the assigned employees’ apps.");
    if (failed.length) setError(failed.join("; "));
    notifySalesUpdated(); await load(); busy.current = false; setSaving(false);
  };
  const report = useMemo(() => buildDeliveryLog(availableSales, { hideEmptyAreas: true, areaOrder }), [availableSales, areaOrder]);
  const groupBills = viewGroup ? pending.filter((row) => viewGroup.ids.includes(String(row.id))) : [];
  const groupSelectedCount = groupBills.filter((row) => selected.includes(String(row.id))).length;
  const moveGroupToSuspense = async () => {
    const saleIds = groupBills.filter((row) => selected.includes(String(row.id))).map((row) => row.id);
    if (busy.current || !saleIds.length) return;
    if (!suspenseReason.trim()) { setSuspenseFeedback("Enter the reason for moving these bills to Suspense."); return; }
    busy.current = true;
    setSaving(true);
    setSuspenseBusy(true);
    setSuspenseFeedback("");
    try {
      const response = await fetch(`${API}/staff/sales/suspense`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ saleIds, suspense: true, reason: suspenseReason.trim() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Unable to move bills to Suspense.");
      setSuspenseReason("");
      if (data.pendingApproval) {
        setSelected((current) => current.filter((id) => !saleIds.map(String).includes(String(id))));
        setSuspenseFeedback(data.message);
        setMessage(data.message);
        return;
      }
      const moved = new Set((data.movedIds || []).map(String));
      setSales((current) => current.filter((row) => !moved.has(String(row.id))));
      setSelected((current) => current.filter((id) => !moved.has(String(id))));
      const feedback = `${moved.size} bill(s) moved to Suspense.${data.skippedIds?.length ? ` ${data.skippedIds.length} skipped because their status changed.` : ""}`;
      setSuspenseFeedback(feedback);
      setMessage(feedback);
      notifySalesUpdated();
      await load();
    } catch (requestError) {
      setSuspenseFeedback(requestError.message || "Unable to move bills to Suspense.");
    } finally {
      busy.current = false;
      setSaving(false);
      setSuspenseBusy(false);
    }
  };
  const [error, setError] = useState("");
  return (
    <DashboardLayout><DashboardNavbar /><MDBox py={3} sx={{ backgroundColor: "#fff" }}>
      <DialogTitle id="delivery-log-title" sx={{ py: 0.5, px: 1, backgroundColor: "#dbeafe", borderBottom: "1px solid #93c5fd" }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
          <MDTypography variant="h6" color="dark">Report — Pending Deliveries</MDTypography>
          <MDBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel id="report-route-label">Select Route</InputLabel>
              <Select labelId="report-route-label" label="Select Route" value={routeId} disabled={saving || !routes.length}
                onChange={(event) => selectRoute(event.target.value)} sx={{ height: 34, backgroundColor: "#fff" }}>
                {routes.map((route) => <MenuItem key={route.id} value={String(route.id)}>{route.name} ({billsForRoute(route).length} bills)</MenuItem>)}
              </Select>
            </FormControl>
            <MDButton size="small" color="info" variant="outlined" onClick={() => setViewOpen(true)}>View ({stagedIds.size})</MDButton>
            <MDButton size="small" color="info" variant="gradient" disabled={saving || !chosen.length} onClick={() => { setError(""); setOpen(true); }}>Assign ({chosen.length})</MDButton>
          </MDBox>
        </MDBox>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <MDBox px={1} py={0.5} bgColor="#eff6ff">
          <MDTypography variant="caption">All companies • Packed and returned bills awaiting delivery • {report.total} pending bills • {boxCount(pending)} boxes • {packetCount(pending)} packets</MDTypography>
          {routeError && <MDTypography variant="caption" display="block" color="warning">{routeError}</MDTypography>}
          {message && <MDTypography variant="caption" display="block" color="success">{message}</MDTypography>}
          {error && <MDTypography variant="caption" display="block" color="error">{error}</MDTypography>}
        </MDBox>
        {!report.companyCount ? <MDBox p={3}><MDTypography variant="button">No delivery data available.</MDTypography></MDBox> : (
          <TableContainer sx={{ maxHeight: "72vh" }}>
            <Table size="small" aria-label="Pending delivery bills by company and area" sx={{ tableLayout: "fixed", width: 160 + (report.width - 1) * 95, borderCollapse: "collapse" }}>
              <colgroup>
                <col style={{ width: 160 }} />
                {Array.from({ length: report.width - 1 }, (_, index) => <col key={index} style={{ width: 95 }} />)}
              </colgroup>
              <TableBody>
                {report.rows.slice(0, report.rows.findIndex((row) => row.type === "spacer")).map(({ type, cells }, index) => (
                  <TableRow key={index}>
                    {(type === "section" || type === "spacer" ? [cells[0] || ""] : cells).map((value, column) => {
                      const bills = typeof value === "number" ? cellBills(type, cells, column) : [];
                      const selectedCount = bills.filter((row) => selected.includes(String(row.id))).length;
                      return <TableCell key={column} colSpan={type === "section" ? 2 : type === "spacer" ? report.width : 1} sx={{
                        px: 0.5, py: type === "spacer" ? 0.25 : 0.35, fontSize: "0.75rem", lineHeight: 1.2, color: "#172033",
                        border: type === "spacer" ? 0 : "1px solid #cbd5e1",
                        backgroundColor: selectedCount > 0 ? "#dcfce7" : `#${deliveryLogColors[type]}`,
                        fontWeight: ["section", "header", "total"].includes(type) ? 700 : 400,
                        textAlign: column === 0 ? "left" : "center", overflowWrap: "anywhere",
                        ...(column === 0 && !["section", "spacer"].includes(type) ? { position: "sticky", left: 0, zIndex: 1, width: 160 } : {}),
                      }}>{typeof value === "number" ? <MDBox><MDBox display="flex" alignItems="center" justifyContent="center" gap={0.25}>
                        <Checkbox size="small" sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 15 } }} disabled={saving || value === 0} checked={value > 0 && cellBills(type, cells, column).every((row) => selected.includes(String(row.id)))} indeterminate={cellBills(type, cells, column).some((row) => selected.includes(String(row.id))) && !cellBills(type, cells, column).every((row) => selected.includes(String(row.id)))} onChange={() => toggle(cellBills(type, cells, column))} inputProps={{ "aria-label": "Select bills: " + cells[0] + " / " + (report.rows[0].cells[column] || "Total") }} />{value}
                        <Tooltip title="View and select bills"><span>
                          <IconButton size="small" disabled={saving || value === 0}
                            sx={{ p: 0.25 }} aria-label={`View bills: ${cells[0]} / ${report.rows[0].cells[column] || "Total"}`}
                            onClick={() => setViewGroup({
                              title: `${cells[0]} / ${report.rows[0].cells[column] || "Total"}`,
                              ids: cellBills(type, cells, column).map((row) => String(row.id)),
                            })}>
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span></Tooltip>
                      </MDBox>
                        {value > 0 && <PackBadges rows={bills} compact />}
                        {selectedCount > 0 && <MDTypography component="span" sx={{ display: "block", color: "#166534", fontSize: "0.65rem", fontWeight: 700, whiteSpace: "nowrap", mt: 0.25 }}>
                          {selectedCount}/{bills.length} selected
                        </MDTypography>}
                      </MDBox> : value}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <Dialog open={Boolean(viewGroup)} onClose={() => { if (!saving) { setViewGroup(null); setSuspenseFeedback(""); } }} fullWidth maxWidth="md">
        <DialogTitle>{viewGroup?.title} — Pending bills</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="caption" display="block" mb={1}>Select bills, then click Done to choose more from another area or company. When finished, use the Assign button at the top of the report.</MDTypography>
          <MDTypography variant="caption" display="block" mb={1}>Suspense moves only the selected bills in this window out of Pending Deliveries. A reason is required; unless you are admin, the request goes to admin and the bills move after approval.</MDTypography>
          <MDInput label="Reason for Suspense" value={suspenseReason} onChange={(event) => setSuspenseReason(event.target.value)} fullWidth multiline minRows={1} inputProps={{ maxLength: 1000 }} disabled={saving} sx={{ mb: 1 }} />
          {suspenseFeedback && <MDTypography role="status" variant="body2" mb={1}>{suspenseFeedback}</MDTypography>}
          <TableContainer sx={{ maxHeight: "55vh" }}>
            <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { fontSize: "0.8125rem" } }}>
              <TableHead sx={{ display: "table-header-group" }}><TableRow>
                <TableCell padding="checkbox"><Checkbox size="small" disabled={saving || !groupBills.length}
                  checked={groupBills.length > 0 && groupSelectedCount === groupBills.length}
                  indeterminate={groupSelectedCount > 0 && groupSelectedCount < groupBills.length}
                  onChange={() => toggle(groupBills)} inputProps={{ "aria-label": "Select all bills in this group" }} /></TableCell>
                <TableCell>Invoice number</TableCell><TableCell>Outlet name</TableCell><TableCell>Area</TableCell><TableCell align="center">Boxes</TableCell><TableCell align="center">Packets</TableCell><TableCell align="right">Price</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {groupBills.map((row) => <TableRow key={row.id} selected={selected.includes(String(row.id))} hover>
                  <TableCell padding="checkbox"><Checkbox size="small" disabled={saving}
                    checked={selected.includes(String(row.id))} onChange={() => toggle([row])}
                    inputProps={{ "aria-label": `Select invoice ${row.invoice_number || row.id}` }} /></TableCell>
                  <TableCell>{row.invoice_number || "N/A"}</TableCell>
                  <TableCell>{row.outlet_name || "Outlet not assigned"}</TableCell>
                  <TableCell>{reportAreaName(row.location_name)}</TableCell>
                  <TableCell align="center">{row.box_count ?? "—"}</TableCell>
                  <TableCell align="center">{row.packet_count ?? "—"}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>Rs. {Number(row.price || 0).toFixed(2)}</TableCell>
                </TableRow>)}
                {!groupBills.length && <TableRow><TableCell colSpan={7}>No pending bills remain in this group.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          <MDTypography variant="caption" sx={{ mr: "auto", pl: 1 }}>Selected here: {groupSelectedCount} / {groupBills.length} · Boxes: {boxCount(groupBills.filter((row) => selected.includes(String(row.id))))} / {boxCount(groupBills)} · Packets: {packetCount(groupBills.filter((row) => selected.includes(String(row.id))))} / {packetCount(groupBills)} · Total selected: {chosen.length} ({boxCount(chosen)} boxes, {packetCount(chosen)} packets)</MDTypography>
          <MDButton color="warning" disabled={saving || !groupSelectedCount || !suspenseReason.trim()} onClick={moveGroupToSuspense}>
            {suspenseBusy ? "Moving…" : `Suspense (${groupSelectedCount})`}
          </MDButton>
          <MDButton color="secondary" disabled={saving} onClick={() => { setViewGroup(null); setSuspenseFeedback(""); }}>Done</MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={open} onClose={() => { if (!saving) setOpen(false); }} fullWidth maxWidth="sm">
        <DialogTitle>Assign {chosen.length} bills</DialogTitle>
        <DialogContent dividers><MDBox display="flex" flexDirection="column" gap={2}>
          {error && <MDTypography variant="body2" color="error">{error}</MDTypography>}
          <FormControl fullWidth>
            <InputLabel id="report-assignee-type-label">Assign To</InputLabel>
            <Select labelId="report-assignee-type-label" label="Assign To" value={assigneeType} disabled={saving} onChange={(event) => { setAssigneeType(event.target.value); setAssigneeCompany(""); setBoy(""); setError(""); }} sx={{ height: 44 }}>
              <MenuItem value="delivery_boy">Delivery Boy</MenuItem>
              <MenuItem value="marketing_staff">Marketing Staff</MenuItem>
            </Select>
          </FormControl>
          {assigneeType === "marketing_staff" && <CompanyTypeSelect
            id="report-assignee-company-type"
            value={assigneeCompanyType}
            disabled={saving}
            required
            onChange={(type) => {
              setAssigneeCompanyType(type);
              if (assigneeCompany && type && !companyMatchesType(assigneeCompany, type)) {
                setAssigneeCompany("");
                setBoy("");
              }
            }}
          />}
          {assigneeType === "marketing_staff" && <FormControl fullWidth disabled={!assigneeCompanyType}>
            <InputLabel id="report-assignee-company-label">Company</InputLabel>
            <Select labelId="report-assignee-company-label" label="Company" value={assigneeCompany} disabled={saving} onChange={(event) => { setAssigneeCompany(event.target.value); setBoy(""); }} sx={{ height: 44 }}>
              {assigneeCompanies.map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
              {!assigneeCompanies.length && <MenuItem disabled>No {assigneeCompanyType === "cnf" ? "CNF" : "Distributor"} companies with active marketing staff</MenuItem>}
            </Select>
          </FormControl>}
          {(assigneeType === "delivery_boy" || (assigneeType === "marketing_staff" && assigneeCompany)) && <FormControl fullWidth>
            <InputLabel id="report-boy-label">{assigneeType === "delivery_boy" ? "Delivery Boy" : "Staff Name"}</InputLabel>
            <Select labelId="report-boy-label" label={assigneeType === "delivery_boy" ? "Delivery Boy" : "Staff Name"} value={boy} disabled={saving} onChange={(event) => setBoy(event.target.value)} sx={{ height: 44 }}>
              {eligibleAssignees.map((person) => <MenuItem key={person.id} value={String(person.id)}>{person.name.replace(/ \(Company staff\)$/, "")}</MenuItem>)}
              {!eligibleAssignees.length && <MenuItem disabled>No active staff available</MenuItem>}
            </Select>
          </FormControl>}
          <MDInput type="date" label="Delivery Date" InputLabelProps={{ shrink: true }} value={date} disabled={saving} onChange={(event) => setDate(event.target.value)} />
          <MDInput label="Vehicle Number" value={vehicle} disabled={saving} onChange={(event) => setVehicle(event.target.value)} />
          <MDTypography variant="caption">This adds bills to your draft. Open View and Submit to send them to the assigned employee’s app. Drafts remain here until you leave or reload this page.</MDTypography>
        </MDBox></DialogContent>
        <DialogActions><MDButton color="secondary" disabled={saving} onClick={() => setOpen(false)}>Cancel</MDButton><MDButton color="info" variant="gradient" disabled={saving || !chosen.length} onClick={stage}>Add to View</MDButton></DialogActions>
      </Dialog>
      <Dialog open={viewOpen} onClose={() => { if (!saving) setViewOpen(false); }} fullWidth maxWidth="lg">
        <DialogTitle>View Assignments</DialogTitle>
        <DialogContent dividers>
          {message && <MDTypography variant="body2" color="success">{message}</MDTypography>}
          {error && <MDTypography variant="body2" color="error">{error}</MDTypography>}
          <MDTypography variant="caption">All bills are selected by default. Unselect any bill you do not want to assign. Submit assigns only selected bills and returns unselected bills to Report. Use the eye icon to view bill details and items.</MDTypography>
          <FormControlLabel label={`Select All (${selectedBillCount}/${stagedIds.size})`} control={<Checkbox
            disabled={saving || !stagedIds.size}
            checked={stagedIds.size > 0 && selectedBillCount === stagedIds.size}
            indeterminate={selectedBillCount > 0 && selectedBillCount < stagedIds.size}
            onChange={(_, checked) => setRemoveIds(checked ? [] : [...stagedIds])}
          />} />
          {!drafts.length && <MDTypography variant="body2" py={2}>No draft assignments. Select bills in Report and click Assign.</MDTypography>}
          <MDBox display="flex" sx={{ overflowX: "auto", mt: 1, alignItems: "stretch" }}>
            {draftBoys.map((boyId) => {
              const batches = drafts.filter((draft) => draft.boy === boyId);
              return <MDBox key={boyId} sx={{ minWidth: 260, flex: "1 0 260px", border: "1px solid #cbd5e1", display: "flex", flexDirection: "column" }}>
                <MDTypography variant="h6" sx={{ backgroundColor: "#16dfe3", p: 1 }}>{batches[0].name}</MDTypography>
                {batches.map((draft) => {
                  const groups = new Map();
                  draft.rows.forEach((row) => {
                    const company = String(row.company_name || "Company not assigned").trim();
                    const area = reportAreaName(row.location_name);
                    const key = JSON.stringify([company, area]);
                    const group = groups.get(key) || { company, area, count: 0, ids: [], rows: [] };
                    group.count += 1; group.ids.push(String(row.id)); group.rows.push(row); groups.set(key, group);
                  });
                  return <MDBox key={draft.id} p={1} sx={{ borderBottom: "1px solid #cbd5e1" }}>
                    <MDTypography variant="caption" display="block">Date: {draft.date} · Vehicle: {draft.vehicle}</MDTypography>
                    {[...groups.entries()].map(([key, group]) => <MDBox key={key} py={0.5}>
                      <MDBox display="flex" justifyContent="space-between" gap={1}>
                      <Checkbox size="small" disabled={saving}
                        checked={group.ids.every((id) => !removeIds.includes(id))}
                        indeterminate={group.ids.some((id) => removeIds.includes(id)) && !group.ids.every((id) => removeIds.includes(id))}
                        onChange={() => toggleDraftArea(group.ids)}
                        inputProps={{ "aria-label": "Select " + group.area + " / " + group.company + " for " + draft.name + " on " + draft.date }} />
                      <MDBox><MDTypography variant="button" display="block">{group.area}</MDTypography><MDTypography variant="caption">{group.company}</MDTypography></MDBox>
                      <MDBox textAlign="right"><MDTypography variant="button" display="block" sx={{ whiteSpace: "nowrap" }}>{group.ids.filter((id) => !removeIds.includes(id)).length}/{group.count} bills</MDTypography>
                      <PackBadges rows={group.rows.filter((row) => !removeIds.includes(String(row.id)))} /></MDBox>
                      </MDBox>
                      {group.rows.map((row) => <MDBox key={row.id} display="flex" alignItems="center" gap={0.5} pl={2} py={0.25}>
                        <Checkbox size="small" disabled={saving} checked={!removeIds.includes(String(row.id))}
                          onChange={() => toggleDraftArea([String(row.id)])}
                          inputProps={{ "aria-label": "Assign bill " + (row.invoice_number || row.id) }} />
                        <MDBox sx={{ flex: 1 }}>
                          <MDTypography variant="caption" display="block">Bill: {row.invoice_number || row.id}</MDTypography>
                          <MDTypography variant="caption" display="block">{row.outlet_name || "Outlet not assigned"}</MDTypography>
                        </MDBox>
                        <MDTypography variant="caption" sx={{ whiteSpace: "nowrap" }}>{row.box_count ?? 0} box · {row.packet_count ?? 0} pkt</MDTypography>
                        <Tooltip title="View bill details"><IconButton size="small" aria-label={"View bill " + (row.invoice_number || row.id)} onClick={() => setViewBill({ row, draft })}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                      </MDBox>)}
                    </MDBox>)}
                    <MDButton size="small" color="error" disabled={saving || !draft.rows.some((row) => removeIds.includes(String(row.id)))} onClick={() => removeSelectedAreas(draft.id)}>Remove unselected bills</MDButton>
                  </MDBox>;
                })}
                <MDTypography variant="button" sx={{ mt: "auto", p: 1, backgroundColor: "#fef3c7", fontWeight: 700 }}>Selected: {batches.reduce((total, draft) => total + draft.rows.filter((row) => !removeIds.includes(String(row.id))).length, 0)} bills · {batches.reduce((total, draft) => total + boxCount(draft.rows.filter((row) => !removeIds.includes(String(row.id)))), 0)} boxes · {batches.reduce((total, draft) => total + packetCount(draft.rows.filter((row) => !removeIds.includes(String(row.id)))), 0)} packets</MDTypography>
              </MDBox>;
            })}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDTypography variant="button" sx={{ mr: "auto", pl: 1 }}>Selected: {selectedBillCount} of {stagedIds.size} bills · {selectedDrafts.reduce((total, draft) => total + boxCount(draft.rows), 0)} boxes · {selectedDrafts.reduce((total, draft) => total + packetCount(draft.rows), 0)} packets</MDTypography>
          <MDButton color="info" variant="outlined" disabled={saving || !selectedBillCount} onClick={() => {
            try { setError(""); printDeliveryAssignmentsPdf(selectedDrafts); }
            catch (err) { setError(err.message); }
          }}>Download PDF</MDButton>
          <MDButton color="secondary" disabled={saving} onClick={() => setViewOpen(false)}>Back</MDButton>
          <MDButton color="info" variant="gradient" disabled={saving || !selectedBillCount} onClick={submit}>{saving ? "Submitting…" : `Submit (${selectedBillCount})`}</MDButton>
        </DialogActions>
      </Dialog>
      <MDBox mt={3} sx={{ borderBottom: "1px solid #cbd5e1" }}>
        <Tabs value={historyTab} onChange={(_, value) => setHistoryTab(value)} sx={{ maxWidth: 900 }}>
          <Tab value="assignments" label="Date-wise Assigned Orders" />
          <Tab value="pending" label="Pending Deliveries" />
          <Tab value="transfers" label="Transfers" />
          <Tab value="download" label="Download Report" />
        </Tabs>
      </MDBox>
      {historyTab === "assignments" && <AssignmentHistory api={API} onViewBill={setViewBill} areaOrder={routeAreaOrder} boys={boys} />}
      {historyTab === "pending" && <PendingDeliveries api={API} />}
      {historyTab === "transfers" && <DeliveryTransfers api={API} />}
      {historyTab === "download" && <DeliveryStatusReport api={API} />}
      <BillDetailsDialog bill={viewBill} onClose={() => setViewBill(null)} api={API} />
    </MDBox><Footer /></DashboardLayout>
  );
}
